import { HttpErrorResponse } from '@angular/common/http';

import { extractMessage } from './form-server-error';

export interface ServerErrorDetails {
  type: string;
  message: string;
  request: string;
  trace: string;
}

/** Developer-facing breakdown of a failed request: what failed, why, and the raw payload. */
export function describeServerError(error: unknown): ServerErrorDetails {
  if (error instanceof HttpErrorResponse) {
    return describeHttpError(error);
  }
  if (error instanceof Error) {
    return { type: error.name, message: error.message, request: '', trace: error.stack ?? '' };
  }
  return { type: typeof error, message: formatBody(error), request: '', trace: '' };
}

function describeHttpError(error: HttpErrorResponse): ServerErrorDetails {
  const body: unknown = error.error;
  const exceptionClass: string | null = findString(body, 'class');
  const status: string = joinParts([error.status, error.statusText]);
  return {
    type: exceptionClass ?? status,
    message: extractMessage(body) ?? findString(body, 'detail') ?? error.message,
    request: exceptionClass === null ? (error.url ?? '') : joinParts([error.status, error.url]),
    trace: formatBody(body),
  };
}

function findString(body: unknown, key: string): string | null {
  for (const node of [body, (body as { error?: unknown } | null)?.error]) {
    const value: unknown = isRecord(node) ? node[key] : undefined;
    if (typeof value === 'string' && value.trim() !== '') {
      return value;
    }
  }
  return null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function joinParts(parts: unknown[]): string {
  return parts.filter((part: unknown): boolean => part !== null && part !== '').join(' ');
}

function formatBody(body: unknown): string {
  if (body === null || body === undefined) {
    return '';
  }
  if (typeof body === 'string') {
    return body;
  }
  try {
    return JSON.stringify(body, null, 2) ?? String(body);
  } catch {
    return String(body);
  }
}
